import * as THREE from 'three';
import { FikoSahne } from '../../IFikoThreeJS/FikoSahne';

/**
 * 📐 CATIA-Style Geometric Tolerance (Geometrik Tolerans)
 * GD&T (Geometric Dimensioning and Tolerancing) annotation
 */

export class FikoGeometrikTolerans extends THREE.Group {
    override type = 'FikoGeometrikTolerans';

    // 🔴 SABİT BOYUT
    sabitBoyut: boolean = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegisti();

    private pozisyon: THREE.Vector3;
    private toleransTipi: string;
    private deger: number;
    private renk: string = '#9C27B0';

    private sprite: THREE.Sprite | null = null;
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;
    private texture: THREE.CanvasTexture;

    constructor(
        public sahne: FikoSahne,
        pozisyon: THREE.Vector3,
        toleransTipi: string = 'flatness',
        deger: number = 0.01
    ) {
        super();

        this.pozisyon = pozisyon.clone();
        this.toleransTipi = toleransTipi;
        this.deger = deger;

        this.canvas = document.createElement('canvas');
        this.canvas.width = 128;
        this.canvas.height = 64;
        const ctx = this.canvas.getContext('2d', { alpha: true });
        if (!ctx) throw new Error('Failed to get 2D context');
        this.context = ctx;
        this.texture = new THREE.CanvasTexture(this.canvas);

        this.name = 'Geometrik Tolerans';
        this.renderOrder = 999;

        this.init();

        if (sahne) {
            this.hazirla(sahne);
        }
    }

    hazirla(sahne?: FikoSahne): void {
        if (!sahne || !sahne.anaGosterim || !sahne.anaGosterim.aktifKamera) return;
        this.sahne = sahne;

        try {
            if (this.sprite) {
                this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
                this.kameraYonDegisti();
            }
            if (!this.hazirlaListenerEklendi && sahne.anaGosterim.aktifKontrol) {
                sahne.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) { /* Ignored */ }
    }

    private kameraYonDegisti(): void {
        if (!this.sabitBoyut || !this.sahne?.anaGosterim?.aktifKamera || !this.sprite) return;
        const originalScale = this.sprite.userData.originalScale as THREE.Vector3;
        if (!originalScale) return;

        const kamera = this.sahne.anaGosterim.aktifKamera;
        const zoom = kamera?.zoom ?? 1;
        const factor = (kamera?.top - kamera?.bottom) / zoom;
        const scaleAdjustment = factor * 0.02;
        this.sprite.scale.set(originalScale.x * scaleAdjustment, originalScale.y * scaleAdjustment, 1);
        this.sprite.updateMatrix();
    }

    setSabitBoyut(aktif: boolean): void {
        this.sabitBoyut = aktif;
        if (aktif && this.sprite) {
            this.sprite.userData.originalScale = new THREE.Vector3().copy(this.sprite.scale);
            this.kameraYonDegisti();
        }
    }

    private init(): void {
        this.createSprite();
    }

    private createSprite(): void {
        const ctx = this.context;
        const text = `⊕ ${this.toleransTipi.toUpperCase()} ${this.deger}`;

        ctx.font = 'bold 14px Arial';
        const metrics = ctx.measureText(text);

        const scale = 2;
        const width = Math.pow(2, Math.ceil(Math.log2(metrics.width * scale + 40)));
        const height = Math.pow(2, Math.ceil(Math.log2(40)));

        this.canvas.width = width;
        this.canvas.height = height;
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.clearRect(0, 0, width / scale, height / scale);

        // Feature control frame (black background with double border)
        ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.fillRect(5, 5, width / scale - 10, height / scale - 10);

        ctx.strokeStyle = this.renk;
        ctx.lineWidth = 3;
        ctx.strokeRect(5, 5, width / scale - 10, height / scale - 10);
        ctx.strokeRect(8, 8, width / scale - 16, height / scale - 16);

        // Text
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = this.renk;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, width / scale / 2, height / scale / 2);

        this.texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: this.texture,
            transparent: true,
            depthTest: false
        });

        this.sprite = new THREE.Sprite(spriteMaterial);
        this.sprite.position.copy(this.pozisyon);
        this.sprite.scale.set(3, 1, 1);
        this.add(this.sprite);
    }

    dispose(): void {
        if (this.sahne?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.sahne.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }
        if (this.sprite) {
            this.sprite.material.map?.dispose();
            this.sprite.material.dispose();
        }
        this.texture.dispose();
    }
}
